import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Link as RouterLink } from '@tanstack/react-router';
import Iconify from '@emfood/shared/src/components/iconify';

import {
  Stack,
  Dialog,
  Button,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';

import { paths } from 'src/routes/paths';

import { ProductIssueType } from 'src/api/models/orders';

import ReportProductIssueForm from './report-product-issue-form';
import ReportDifferentProductForm from './report-different-product-form';

// Props for the ReportProductIssue component
interface ReportProductIssueDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  orderId: string;
  productId: string;
  productName: string;
  productUnits: string;
  productCost: string;
  productQuantity: number;
}

const ReportProductIssueDialog: React.FC<ReportProductIssueDialogProps> = ({
  open,
  onClose,
  onSubmit,
  orderId,
  productId,
  productName,
  productUnits,
  productCost,
  productQuantity,
}) => {
  const [exitConfirmationIsOpen, setExitConfirmationIsOpen] = useState(false);
  const [goBackConfirmationIsOpen, setGoBackConfirmationIsOpen] = useState(false);
  const [successConfirmationIsOpen, setSuccessConfirmationIsOpen] = useState(false);
  const [issueType, setIssueType] = useState<ProductIssueType | null>(null);

  type DialogOption = {
    title: string;
    description: string;
    issueId: ProductIssueType;
    icon: string;
  };

  const modalOptions: DialogOption[] = [
    {
      title: 'Quantity',
      description: 'Incorrect quantity received',
      issueId: ProductIssueType.Quantity,
      icon: 'mdi:clipboard-list-outline',
    },
    {
      title: 'Quality',
      description: 'Product quality is not as expected',
      issueId: ProductIssueType.Quality,
      icon: 'mdi:food-apple-outline',
    },
    {
      title: 'Different Product',
      description: 'Received a different product than ordered',
      issueId: ProductIssueType['Different Product'],
      icon: 'mdi:package-variant-closed-minus',
    },
    {
      title: 'Shelf Life',
      description: 'Product has a shorter shelf-life than expected',
      issueId: ProductIssueType['Shelf Life'],
      icon: 'mdi:clock-remove-outline',
    },
    {
      title: 'Packaging',
      description: 'Packaging was damaged or inadequate',
      issueId: ProductIssueType.Packaging,
      icon: 'mdi:package-variant-remove',
    },
    {
      title: 'Specification',
      description: 'Product does not match the specification',
      issueId: ProductIssueType.Specification,
      icon: 'mdi:format-list-bulleted',
    },
    {
      title: 'Temperature',
      description: 'Product was not at the correct temperature',
      issueId: ProductIssueType.Temperature,
      icon: 'mdi:thermometer-alert',
    },
    {
      title: 'Feedback',
      description: 'General feedback about the product',
      issueId: ProductIssueType.Feedback,
      icon: 'mdi:feedback-outline',
    },
  ];
  const renderFormPage = () => {
    if (issueType === ProductIssueType['Different Product']) {
      return (
        <ReportDifferentProductForm
          orderId={orderId}
          productId={productId}
          productName={productName}
          productCost={productCost}
          productQuantity={productQuantity}
          issueType={issueType}
          issueTypeDescription={
            modalOptions.find((el) => el.issueId === issueType)?.description ?? ''
          }
          goBack={(isDirty) => {
            if (isDirty) {
              setGoBackConfirmationIsOpen(true);
            } else {
              setIssueType(null);
            }
          }}
          onSuccess={() => {
            setSuccessConfirmationIsOpen(true);
            onSubmit();
          }}
          onCancel={(isDirty) => {
            if (isDirty) {
              setExitConfirmationIsOpen(true);
            } else {
              onClose();
              setIssueType(null);
            }
          }}
        />
      );
    }
    const option = modalOptions.find((el) => el.issueId === issueType)!;

    return (
      <ReportProductIssueForm
        issueType={{
          name: option.title,
          description: option.description,
          id: option.issueId,
        }}
        goBack={(isDirty) => {
          if (isDirty) {
            setGoBackConfirmationIsOpen(true);
          } else {
            setIssueType(null);
          }
        }}
        orderId={orderId}
        productId={productId}
        productName={productName}
        productUnits={productUnits}
        onSuccess={() => {
          setSuccessConfirmationIsOpen(true);
          onSubmit();
        }}
        onCancel={(isDirty) => {
          if (isDirty) {
            setExitConfirmationIsOpen(true);
          } else {
            onClose();
            setIssueType(null);
          }
        }}
      />
    );
  };

  return createPortal(
    <>
      <Dialog open={open && !successConfirmationIsOpen} onClose={onClose} fullWidth>
        {issueType === null ? (
          <>
            <DialogTitle>What issue would you like to report about this product?</DialogTitle>
            <DialogContent>
              <Typography variant="body1">{productName}</Typography>
              <Typography variant="body1" color="GrayText" sx={{ mb: 3 }}>
                {productUnits}
              </Typography>
              {modalOptions.map((option) => (
                <Button
                  key={option.title}
                  onClick={() => setIssueType(option.issueId)}
                  startIcon={<Iconify width={24} icon={option.icon} />}
                  fullWidth
                  variant="outlined"
                  sx={{ justifyContent: 'flex-start', mb: 1, px: 2 }}
                >
                  <div style={{ textAlign: 'left' }}>
                    <Typography variant="body1">{option.title}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {option.description}
                    </Typography>
                  </div>
                </Button>
              ))}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => onClose()}>Cancel</Button>
            </DialogActions>
          </>
        ) : (
          renderFormPage()
        )}
      </Dialog>
      <Dialog open={exitConfirmationIsOpen} onClose={() => setExitConfirmationIsOpen(false)}>
        <DialogTitle>
          You have an unfinished issue log. Would you like to continue to submit your issue or
          discard and cancel your issue log?
        </DialogTitle>
        <DialogActions>
          <Button
            onClick={() => {
              onClose();
              setExitConfirmationIsOpen(false);
              setIssueType(null);
            }}
          >
            Discard
          </Button>
          <Button onClick={() => setExitConfirmationIsOpen(false)} variant="contained">
            Continue
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={goBackConfirmationIsOpen} onClose={() => setGoBackConfirmationIsOpen(false)}>
        <DialogTitle>
          You have an unfinished issue log. Would you like to continue to submit your issue or go
          back and lose your progress?
        </DialogTitle>
        <DialogActions>
          <Button
            onClick={() => {
              setGoBackConfirmationIsOpen(false);
              setIssueType(null);
            }}
          >
            Discard
          </Button>
          <Button onClick={() => setGoBackConfirmationIsOpen(false)} variant="contained">
            Continue
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={successConfirmationIsOpen} onClose={() => setSuccessConfirmationIsOpen(false)}>
        <DialogTitle>Thank you.</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1" gutterBottom>
            Your product issue has been submitted. We will contact you with a resolution shortly.
          </Typography>
          <Typography gutterBottom>
            A record has been sent to your email and is stored in the issue log.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Stack gap={2} sx={{ width: '100%' }}>
            <Button
              variant="outlined"
              LinkComponent={RouterLink}
              href={paths.dashboard.issues.root}
            >
              View Issue Log
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                onClose();
                setSuccessConfirmationIsOpen(false);
              }}
            >
              Close
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </>,
    document.body
  );
};

export default ReportProductIssueDialog;
